import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Descriptions, Drawer, Form, Input, Modal, Row, Select, Space, Table, Tag, Typography, message } from 'antd';
import { ExportOutlined, EyeOutlined, ReloadOutlined, SettingOutlined, ExperimentOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import JobMatchPreview from './JobMatchPreview';

const { Title, Text } = Typography;

function Sites() {
  const { user, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const canConfigureSource = user?.role === 'super_admin';
  const settingsPanel = canConfigureSource ? searchParams.get('settings') : null;
  const canRead = hasPermission('sites', 'read');
  const [searchForm] = Form.useForm();
  const [sites, setSites] = useState([]);
  const [filters, setFilters] = useState({ status: 'recruiting' });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const { current, pageSize } = pagination;
  const [total, setTotal] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (canConfigureSource && ['source', 'push'].includes(settingsPanel)) {
      navigate(settingsPanel === 'push' ? '/push-records/submission-settings' : '/settings/job-source', { replace: true });
    }
  }, [canConfigureSource, settingsPanel, navigate]);

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get('/sites', { params: { ...filters, page: current, limit: pageSize } }).then(response => {
      if (!cancelled) {
        setSites(response.data.sites);
        setTotal(response.data.pagination.total);
      }
    }).catch(requestError => {
      if (!cancelled) {
        setSites([]);
        setTotal(0);
        setError(requestError.response?.data?.message || '岗位查询失败，请重试');
      }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canRead, filters, current, pageSize, refresh]);

  if (!canRead) return <Alert type="error" showIcon message="没有岗位查看权限" />;

  const openSettings = panel => {
    if (['source', 'push'].includes(panel)) { navigate(panel === 'push' ? '/push-records/submission-settings' : '/settings/job-source'); return; }
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      if (panel) next.set('settings', panel);
      else next.delete('settings');
      return next;
    });
  };

  const search = values => {
    setFilters(values);
    setPagination(previous => ({ ...previous, current: 1 }));
  };

  const exportPage = async () => {
    try {
      setExporting(true);
      const response = await api.get('/sites/export/excel', { params: { ...filters, page: pagination.current, limit: pagination.pageSize }, responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = '实时岗位_当前页.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch { message.error('导出失败，请检查岗位数据源后重试'); }
    finally { setExporting(false); }
  };

  const showDetails = async record => {
    try { setDetail((await api.get(`/sites/${record.sourceJobId}`)).data); }
    catch (requestError) { message.error(requestError.response?.data?.message || '获取岗位详情失败'); }
  };

  const columns = [
    { title: '岗位名称', dataIndex: 'name', width: 250 },
    { title: '城市', dataIndex: 'city', width: 90 },
    { title: '业务线', dataIndex: 'businessLine', width: 100 },
    { title: '加盟商', dataIndex: 'franchiseeName', width: 120 },
    { title: '用工类型', dataIndex: 'employmentType', width: 100 },
    { title: '招聘状态', dataIndex: 'recruitmentStatus', width: 100, render: value => <Tag color={value === 'recruiting' ? 'green' : 'default'}>{value === 'recruiting' ? '招聘中' : '停招'}</Tag> },
    { title: '伙伴通岗位编码', dataIndex: 'externalJobCode', width: 140, render: value => value || <Text type="secondary">未提供</Text> },
    { title: '操作', key: 'actions', width: 116, fixed: 'right', render: (_, record) => (
      <Space wrap>
        <Button icon={<EyeOutlined />} onClick={() => showDetails(record)}>详情</Button>
      </Space>
    ) }
  ];

  return (
    <div>
      <div className="page-header">
        <Row justify="space-between" align="top" gutter={[16, 16]}>
          <Col flex="auto">
            <Title level={2} style={{ marginTop: 0 }}>实时岗位</Title>
            <div className="description">实时查询伙伴通岗位，核对城市、站点与招聘状态。岗位资料在伙伴通维护。</div>
          </Col>
          {canConfigureSource && <Col>
            <Space wrap><Button type="primary" icon={<ExperimentOutlined />} onClick={() => openSettings('match')}>试解析报名</Button>
              <Button icon={<SettingOutlined />} onClick={() => openSettings('source')}>岗位查询设置</Button></Space>
          </Col>}
        </Row>
      </div>
      {error && <Alert type="error" showIcon message={error} action={<Button onClick={() => setRefresh(value => value + 1)}>重试</Button>} style={{ marginBottom: 16 }} />}
      <Card style={{ marginBottom: 16 }}>
        <Form form={searchForm} layout="vertical" initialValues={{ status: 'recruiting' }} onFinish={search}>
          <Row gutter={16}>
            <Col xs={24} sm={12} lg={8}><Form.Item label="关键词" name="search"><Input allowClear maxLength={200} placeholder="岗位名称、站点或地址" /></Form.Item></Col>
            <Col xs={24} sm={12} lg={4}><Form.Item label="城市" name="city"><Input allowClear maxLength={200} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={4}><Form.Item label="业务线" name="businessLine"><Input allowClear maxLength={200} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={4}><Form.Item label="加盟商" name="company"><Input allowClear maxLength={200} /></Form.Item></Col>
            <Col xs={24} sm={12} lg={4}><Form.Item label="招聘状态" name="status"><Select options={[{ value: 'recruiting', label: '招聘中' }, { value: 'closed', label: '停招' }, { value: 'all', label: '全部' }]} /></Form.Item></Col>
          </Row>
          <Space wrap>
            <Button type="primary" htmlType="submit" loading={loading}>查询</Button>
            <Button onClick={() => { searchForm.resetFields(); search({ status: 'recruiting' }); }}>重置</Button>
            <Button icon={<ReloadOutlined />} disabled={loading} onClick={() => setFilters(previous => ({ ...previous }))}>刷新</Button>
            <Button icon={<ExportOutlined />} loading={exporting} disabled={loading || !sites.length} onClick={exportPage}>导出当前页</Button>
          </Space>
        </Form>
      </Card>
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Table rowKey="sourceJobId" columns={columns} dataSource={sites} loading={loading} scroll={{ x: 1100 }} tableLayout="fixed"
            locale={{ emptyText: error ? '岗位查询失败，请重试' : '没有符合条件的岗位' }}
            pagination={{ ...pagination, total, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100], showTotal: count => `共 ${count} 个岗位`,
              onChange: (current, pageSize) => setPagination({ current, pageSize }) }} />
        </Space>
      </Card>
      {canConfigureSource && <Drawer title="试解析报名" placement="right" width="min(800px, 100vw)"
        open={settingsPanel === 'match'} onClose={() => openSettings(null)} destroyOnHidden>
        {settingsPanel === 'match' && <JobMatchPreview />}
      </Drawer>}
      <Modal title="实时岗位详情" open={Boolean(detail)} onCancel={() => setDetail(null)} footer={<Button onClick={() => setDetail(null)}>关闭</Button>} width={720}>
        {detail && <Descriptions column={1} bordered>
          <Descriptions.Item label="岗位名称">{detail.name}</Descriptions.Item>
          <Descriptions.Item label="招聘状态">{detail.isActive ? '招聘中' : '停招'}</Descriptions.Item>
          <Descriptions.Item label="站点">{detail.stationName || '-'}</Descriptions.Item>
          <Descriptions.Item label="城市 / 业务线">{detail.city} / {detail.businessLine}</Descriptions.Item>
          <Descriptions.Item label="加盟商">{detail.franchiseeName || '-'}</Descriptions.Item>
          <Descriptions.Item label="站点地址">{detail.siteAddress || '-'}</Descriptions.Item>
          <Descriptions.Item label="薪资方案"><span style={{ whiteSpace: 'pre-wrap' }}>{detail.salaryPlan || '-'}</span></Descriptions.Item>
          <Descriptions.Item label="伙伴通岗位编码">{detail.externalJobCode || '未提供'}</Descriptions.Item>
        </Descriptions>}
      </Modal>
    </div>
  );
}

export default Sites;
